import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import Cookies from "js-cookie";
import SubscriptionPopup from "../pages/UploadFile/SubscriptionPopup";
import api from "../pages/chat/api";
import { fetchRfpSubscription } from "../services/rfpBillingApi";
import {
  fetchAndCacheSubscriptionStatus,
  getCachedSubscriptionStatus,
  hasPersonalPaidPlan,
} from "../utils/subscriptionStatus";

// Routes where the subscription popup should never appear (unauthenticated /
// public flows). Match by exact path or known prefix.
const SUPPRESSED_PREFIXES = [
  "/signin",
  "/signup",
  "/login",
  "/reset-password",
  "/forgot",
  "/privacy",
  "/terms",
  "/payment",          // also covers /payment-success and /payment-failure
  "/accept-invitation",
  "/account/billing",  // where "View plans" sends them: the one app page left open while blocked
  "/extractor",        // Extractor Studio has its own plan: each task checks the user's credits
];

const RECHECK_MS = 5 * 60 * 1000;

// The last plan seen per workspace, so switching to one (or reloading) can lock or unlock the
// screen straight away instead of after a round trip; the server's answer then confirms it.
const PLAN_CACHE_KEY = "rfpPlanByWorkspace";

const readPlanCache = () => {
  try {
    return JSON.parse(localStorage.getItem(PLAN_CACHE_KEY)) || {};
  } catch (_) {
    return {};
  }
};

const rememberPlan = (workspaceId, plan) => {
  try {
    const all = readPlanCache();
    const { state, can_use, trial_ends_at, current_period_end, unit_amount, currency } = plan;
    all[workspaceId] = { state, can_use, trial_ends_at, current_period_end, unit_amount, currency };
    localStorage.setItem(PLAN_CACHE_KEY, JSON.stringify(all));
  } catch (_) {
    // storage full or blocked: the server's answer still applies, just not instantly next time
  }
};

// What's known about a workspace's plan before asking: the workspace list carries its current
// state (rfp_state / rfp_can_use); the remembered plan fills in the dates and price.
const knownPlan = (workspaceId, workspace) => {
  const remembered = readPlanCache()[workspaceId];
  if (typeof workspace?.rfp_can_use === "boolean") {
    return { ...remembered, state: workspace.rfp_state, can_use: workspace.rfp_can_use };
  }
  return remembered || null;
};

// Who is logged in, from the access token: the same through token refreshes, different when another
// account logs in on this browser.
const currentUserId = () => {
  const token = Cookies.get("access_token");
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return payload.user_id ?? null;
  } catch (_) {
    return null;
  }
};

const activeWorkspaceFromStorage = () => {
  try {
    return JSON.parse(localStorage.getItem("activeWorkspace"));
  } catch (_) {
    return null;
  }
};

const isSuppressedPath = (pathname) =>
  SUPPRESSED_PREFIXES.some((prefix) => pathname.startsWith(prefix));

// Only the workspace's RFP plan locks the app. The old per-user 14-day trial no longer does: RFP
// Manager and Extractor Studio have their own plans, and new accounts don't get that trial at all.
const SubscriptionPopupHost = () => {
  // The current workspace's RFP plan, as GET billing/rfp/subscription/ reports it.
  const [workspacePlan, setWorkspacePlan] = useState(() =>
    knownPlan(Cookies.get("selected_org_id"), activeWorkspaceFromStorage())
  );
  const lastWorkspaceCheck = useRef(0);
  const { pathname } = useLocation();
  // The user the state above belongs to. After another account logs in on this browser, what's
  // left over describes the previous one, so nothing blocks until the new user's own status is in.
  const [stateUserId, setStateUserId] = useState(currentUserId);
  const userId = currentUserId();
  const userChanged = userId !== stateUserId;

  const checkWorkspace = useCallback(() => {
    const workspaceId = Cookies.get("selected_org_id");
    if (!Cookies.get("access_token") || !workspaceId) return;
    lastWorkspaceCheck.current = Date.now();
    fetchRfpSubscription()
      .then((plan) => {
        rememberPlan(workspaceId, plan);
        // A late answer for a workspace they've already switched away from is ignored.
        if (Cookies.get("selected_org_id") === workspaceId) setWorkspacePlan(plan);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!userChanged) return undefined;
    let cancelled = false;
    const settle = () => {
      if (!cancelled) setStateUserId(userId);
    };
    setWorkspacePlan(knownPlan(Cookies.get("selected_org_id"), activeWorkspaceFromStorage()));
    if (!userId) {
      settle();
      return undefined;
    }
    checkWorkspace();
    // The new user's own status, for hasPersonalPaidPlan() below.
    fetchAndCacheSubscriptionStatus(api).then(settle, settle);
    return () => {
      cancelled = true;
    };
  }, [userChanged, userId, checkWorkspace]);

  useEffect(() => {
    checkWorkspace();
    // Lock or unlock for the new workspace at once, from what's already known, then confirm.
    const onWorkspaceChanged = (e) => {
      setWorkspacePlan(knownPlan(Cookies.get("selected_org_id"), e?.detail));
      checkWorkspace();
    };
    window.addEventListener("workspace-changed", onWorkspaceChanged);
    return () => window.removeEventListener("workspace-changed", onWorkspaceChanged);
  }, [checkWorkspace]);

  const workspaceLocked =
    Boolean(workspacePlan) &&
    !workspacePlan.can_use &&
    !hasPersonalPaidPlan(getCachedSubscriptionStatus());
  const blocked = !userChanged && workspaceLocked && !isSuppressedPath(pathname);

  // On every page change, look at the workspace's plan again — right away while blocked, since
  // paying or joining a workspace changes it, otherwise every few minutes.
  useEffect(() => {
    if (blocked || Date.now() - lastWorkspaceCheck.current > RECHECK_MS) checkWorkspace();
  }, [pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!blocked) return null;
  return <SubscriptionPopup reason={workspacePlan.state} plan={workspacePlan} />;
};

export default SubscriptionPopupHost;
